import { formatSum } from '../../../../../utils'
import { formatMskDateTime } from '../../../matching/routes/Dashboard/core/mskTime'
import { ConciergeHistoryItem, ConciergeIssuance, ConciergeLateCredit, ConciergeRoutingChain, ConciergeStage } from '../../core/_conciergeModels'

const STAGE_BY_STATUS: Record<string, ConciergeStage> = {
  CLIENT_PENDING: 'waiting-client',
  MANUAL_REQUISITE: 'manual',
  ADAPTER_PENDING: 'searching',
  WAITING_FOR_REQUISITE: 'searching',
  WAITING_FOR_VARIANT_SELECTION: 'variants',
  PAYMENT_PENDING: 'issued',
  WAITING_FOR_SMS: 'issued',
  WAITING_FOR_ACTION: 'issued',
  FILES_AUTO_VERIFICATION: 'receipt',
  FILES_VERIFICATION: 'receipt',
  COMPLETED: 'final',
  DECLINED: 'final',
  EXPIRED: 'final',
}

/** Which part of the concierge window the ticket status calls for. */
export const conciergeStage = (status: string): ConciergeStage => STAGE_BY_STATUS[status] ?? 'final'

const METHOD_LABELS: Record<string, string> = {
  'SBP P2P RUB': 'СБП',
  'MC/VISA/MIR P2P RUB': 'Карта',
}

export const conciergeMethodLabel = (method: string | null): string =>
  method === null ? '—' : METHOD_LABELS[method] ?? method

/**
 * Methods staff may pick: the shared lookup (enabled, not hidden) narrowed to what the backend accepts
 * for this deposit, under the methods' own names. Until the lookup answers, nothing is offered.
 */
export const conciergeMethodOptions = (lookup: string[] | null, allowed: string[]): Array<{ value: string; label: string }> =>
  (lookup ?? []).filter((m) => allowed.includes(m)).map((m) => ({ value: m, label: m }))

/**
 * Adapters left out of the next search. Every available adapter starts checked; what staff unchecked
 * earlier stays unchecked if it is still in the chain.
 */
export const routingExclusions = (chain: ConciergeRoutingChain | null, previous: number[]): number[] => {
  const ids = (chain?.adapters ?? []).map((a) => a.id).filter((id): id is number => id !== null)

  return previous.filter((id) => ids.includes(id))
}

/** Plain spaces: the text is pasted into a chat, where a non-breaking space shows as garbage. */
const plainSum = (amount: number): string => formatSum(amount).replace(/[  ]/g, ' ')

export type ConciergeIssuanceLine = { label: string; value: string; link?: boolean }

/** The issued requisite as the window shows it: only the lines that have a value. */
export const conciergeIssuanceLines = (issuance: ConciergeIssuance, currency: string): ConciergeIssuanceLine[] =>
  [
    { label: 'Способ', value: conciergeMethodLabel(issuance.method) },
    issuance.requisite ? { label: 'Реквизит', value: issuance.requisite } : null,
    issuance.paymentUrl ? { label: 'Ссылка на оплату', value: issuance.paymentUrl, link: true } : null,
    issuance.bank ? { label: 'Банк', value: issuance.bank } : null,
    issuance.holder ? { label: 'Получатель', value: issuance.holder } : null,
    issuance.amount !== null ? { label: 'Сумма', value: `${plainSum(issuance.amount)} ${currency}` } : null,
  ].filter((line): line is ConciergeIssuanceLine => line !== null)

/**
 * What «Скопировать» puts on the clipboard: the link alone when the adapter answered with one, so it
 * can be pasted straight into a browser; otherwise the requisite alone.
 */
export const conciergeCopyValue = (issuance: ConciergeIssuance): string => issuance.paymentUrl ?? issuance.requisite ?? ''

/** What the window says about a payment on a requisite staff no longer hold. */
export const lateCreditText = (offer: ConciergeLateCredit, currency: string): string => {
  switch (offer.mode) {
    case 'CONFIRMED':
      return `${offer.adapter} подтвердил оплату${offer.amount !== null ? `: ${plainSum(offer.amount)} ${currency}` : ''}. Заявка закроется этой суммой.`
    case 'AWAITING_CONFIRMATION':
      return `${offer.adapter} пока не подтвердил оплату. Зачесть можно после подтверждения.`
    default:
      return `Укажите сумму, которая пришла (${offer.adapter}).`
  }
}

const ACTION_LABELS: Record<string, string> = {
  METHOD_SET: 'Метод оплаты',
  AMOUNT_SET: 'Сумма',
  SEARCH_STARTED: 'Поиск реквизита',
  ISSUED: 'Выдан реквизит',
  ISSUANCE_CANCELLED: 'Выдача отменена',
  DEADLINE_EXTENDED: 'Срок продлён',
  LATE_CREDITED: 'Зачтена поздняя оплата',
  EXPIRED_BY_STAFF: 'Закрыта сотрудником',
  LATE_PAYMENT_SEEN: 'Оплата пришла на прежний реквизит',
  CLIENT_RETURNED: 'Клиент вернулся в сервис',
}

export const conciergeActionLabel = (action: string): string => ACTION_LABELS[action] ?? action

const shown = (value: unknown): string => (value === null || value === undefined || value === '' ? '—' : String(value))

/** One history line: the action and, where the payload has it, what changed. */
export const conciergeHistoryText = (item: ConciergeHistoryItem): string => {
  const label = conciergeActionLabel(item.action)
  const p = item.payload ?? {}

  switch (item.action) {
    case 'METHOD_SET':
    case 'AMOUNT_SET':
      return `${label}: ${shown(p.from)} → ${shown(p.to)}`
    case 'SEARCH_STARTED':
      return `${label}: ${shown(p.method)}, ${shown(p.amount)}`
    case 'DEADLINE_EXTENDED':
      return p.minutes ? `${label} на ${p.minutes} мин` : label
    case 'LATE_CREDITED':
    case 'LATE_PAYMENT_SEEN':
      return p.amount ? `${label}: ${p.amount}` : label
    default:
      return label
  }
}

/** Server moments go through the one shared parser and Moscow formatter. */
export const formatMsk = (iso: string): string => formatMskDateTime(iso)
